import { CreditCard, Dashboard, DomainVerification, Key, People, Person, RocketLaunch, Settings } from '@mui/icons-material';
import {
    Box,
    Divider,
    Drawer,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    Toolbar,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '@/config/app.config';
import { useAuthStore } from '@/stores/use-auth-store';

const SIDEBAR_WIDTH = 240;

const menuItems = [
    { label: 'menu.dashboard', path: ROUTES.dashboard, icon: <Dashboard /> },
    { label: 'menu.setupGuide', path: ROUTES.setupGuide, icon: <RocketLaunch /> },
    { label: 'menu.sapEnvironments', path: ROUTES.sapEnvironments, icon: <Settings /> },
    { label: 'menu.apiKeys', path: ROUTES.apiKeys, icon: <Key /> },
    { label: 'menu.purchase', path: ROUTES.purchase, icon: <CreditCard /> },
    { label: 'menu.profile', path: ROUTES.profile, icon: <Person /> },
];

const adminMenuItems = [
    { label: 'menu.adminUsers', path: ROUTES.adminUsers, icon: <People /> },
    { label: 'menu.adminDomains', path: ROUTES.adminDomains, icon: <DomainVerification /> },
];

interface SidebarProps {
    open: boolean;
    onClose: () => void;
}

export function Sidebar({ open, onClose }: SidebarProps) {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuthStore();
    const isAdmin = user?.role === 'admin';

    const handleNavigate = (path: string) => {
        navigate(path);
        if (isMobile) {
            onClose();
        }
    };

    const renderMenuItems = (items: Array<{ label: string; path: string; icon: React.ReactNode }>) =>
        items.map((item) => (
            <ListItemButton
                key={item.path}
                onClick={() => handleNavigate(item.path)}
                selected={location.pathname === item.path}
                sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    '&.Mui-selected': {
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        '& .MuiListItemIcon-root': {
                            color: 'primary.contrastText',
                        },
                        '&:hover': {
                            bgcolor: 'primary.dark',
                        },
                    },
                }}
            >
                <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                <ListItemText
                    primary={t(item.label)}
                    primaryTypographyProps={{ fontWeight: 500, fontSize: '0.875rem' }}
                />
            </ListItemButton>
        ));

    const drawerContent = (
        <Box sx={{ overflow: 'auto' }}>
            <Toolbar />
            <List sx={{ px: 1, pt: 1 }}>
                {renderMenuItems(menuItems)}
            </List>
            {isAdmin && (
                <>
                    <Divider sx={{ mx: 1 }} />
                    <List
                        sx={{ px: 1 }}
                        subheader={
                            <ListSubheader
                                sx={{
                                    bgcolor: 'transparent',
                                    fontWeight: 700,
                                    fontSize: '0.7rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: 1,
                                    lineHeight: '36px',
                                }}
                            >
                                {t('menu.admin')}
                            </ListSubheader>
                        }
                    >
                        {renderMenuItems(adminMenuItems)}
                    </List>
                </>
            )}
        </Box>
    );

    return (
        <Drawer
            variant={isMobile ? 'temporary' : 'persistent'}
            open={open}
            onClose={onClose}
            sx={{
                width: open ? SIDEBAR_WIDTH : 0,
                flexShrink: 0,
                transition: 'width 225ms cubic-bezier(0.4, 0, 0.6, 1)',
                '& .MuiDrawer-paper': {
                    width: SIDEBAR_WIDTH,
                    boxSizing: 'border-box',
                    borderRight: '1px solid',
                    borderColor: 'divider',
                },
            }}
        >
            {drawerContent}
        </Drawer>
    );
}

export default Sidebar;
